import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Nota,
  PageHeader,
  Skeleton,
} from '../../../ui';

import { TI } from '../routes';
import { CronCatalogoItem, fetchCronCatalogo } from '../../../api/cron-catalogo/cronCatalogoApi';
import { extrairMensagemErro } from '../../../utils/apiError';
import { formatarDataHora } from '../../../utils/data';
import {
  CADENCIAS_EM_ORDEM,
  CadenciaCron,
  ROTULO_CADENCIA,
  cadenciaDoCron,
  ordemDeExecucao,
} from '../../../utils/cronCatalogoRegras';
import { rotuloOrigemExecucao, rotuloStatusExecucao } from '../../../utils/syncExecucao';

/**
 * `/ti/crons` — **Crons do Sistema** (at-34; reescrita na at-70, frente D/D8).
 *
 * Catálogo de todas as rotinas automáticas: quando rodam, o que fazem, o que
 * acontece quando falham, a próxima execução e — para as três que gravam histórico
 * — a última. Read-only: quem dispara e acompanha execuções são os cards da tela
 * de Administração.
 *
 * ## O que mudou na at-70
 *
 *   1. 🔴 **O status da última execução era um valor de MÁQUINA pintado por hex.**
 *      A tela mostrava `CONCLUIDO`, `EM_ANDAMENTO`, `FALHA` e `INTERROMPIDO` em
 *      maiúsculas, com um mapa de cores próprio (`#16a34a`, `#1d4ed8`, `#dc2626`,
 *      `#d97706`) aplicado por `style`. Desde a at-67 existe
 *      `utils/syncExecucao.ts`, que traduz **o mesmo vocabulário das mesmas três
 *      tabelas de run** para `Chip` com texto — esta tela ficou de fora daquela
 *      unificação só por escopo. O azul de `EM_ANDAMENTO` nem existia nos tokens.
 *   2. 🐛 **Erro de carga era indistinguível de catálogo vazio**: um `toast.error`
 *      (que passa) e, no lugar da lista, o texto "Não foi possível carregar" no
 *      mesmo ramo que serviria ao vazio — sem nenhum "Tentar novamente".
 *   3. **Os crons ativos passaram a ser AGRUPADOS por cadência** e ordenados por
 *      horário (`utils/cronCatalogoRegras.ts`). A introdução da tela já explicava
 *      essa divisão em prosa; a lista saía na ordem do array do backend, em que o
 *      minuto 20 vinha depois do minuto 25 e do desativado.
 *   4. **Saiu o último emoji da interface do app** (o `⚠️` da observação — regra de
 *      Victor, 06/09/2026). A observação virou `Nota atencao`, que é exatamente o
 *      que ela é: ressalva sobre conteúdo legítimo.
 *   5. **A explicação da "próxima execução" saiu do `title`** — que não existe no
 *      celular (at-54) — e virou parte do rótulo.
 *   6. **Botão "Atualizar"**, sem cobrir a lista: a última execução muda ao longo
 *      do dia e não havia como recarregar sem F5.
 *   7. Sumiram o `Header` legado, a logo de fundo, o `FullScreenLoader`, a sexta
 *      cópia de `formatarDataHora` e o `useSelector(accessToken)` como guarda
 *      (redundante desde a at-17 — ele fazia a tela abrir **muda** sem token).
 *
 * ⚠️ **O conteúdo dos cards vem PRONTO do backend** (`cron-catalogo.service.ts`,
 * catálogo declarativo): nome, agenda, descrição, comportamento em falha,
 * observação. Esta tela não interpreta nada — se um texto estiver errado, o lugar
 * de corrigir é lá (foi o que a at-70 fez com três deles).
 */

type EstadoLista = 'carregando' | 'ok' | 'erro';

/** Como a carga foi disparada — decide o que a tela esconde enquanto espera. */
type ModoDeCarga = 'inicial' | 'atualizar';

const CronsPage: React.FC = () => {
  const voltar = useVoltar(TI());

  const [crons, setCrons] = useState<CronCatalogoItem[]>([]);
  const [estado, setEstado] = useState<EstadoLista>('carregando');
  const [erro, setErro] = useState('');
  const [atualizando, setAtualizando] = useState(false);
  const [atualizadoEm, setAtualizadoEm] = useState<string | null>(null);

  const carregar = useCallback(async (modo: ModoDeCarga = 'inicial') => {
    if (modo === 'inicial') setEstado('carregando');
    if (modo === 'atualizar') setAtualizando(true);

    try {
      const dados = await fetchCronCatalogo();
      setCrons(dados);
      setAtualizadoEm(new Date().toISOString());
      setErro('');
      setEstado('ok');
    } catch (falha) {
      // A lista é DESCARTADA no erro: manter a anterior faria dado velho passar
      // por atual — e aqui "próxima execução" é justamente o que envelhece.
      setCrons([]);
      setErro(extrairMensagemErro(falha, ''));
      setEstado('erro');
    } finally {
      if (modo === 'atualizar') setAtualizando(false);
    }
  }, []);

  useEffect(() => {
    void carregar('inicial');
  }, [carregar]);

  /**
   * Os grupos, cada um já ordenado por horário.
   *
   * ⚠️ Grupo vazio **não** é renderizado: um título "Desativados" sobre nada diria
   * que a seção existe e está vazia, quando o certo é ela não existir.
   */
  const grupos = useMemo(
    () =>
      CADENCIAS_EM_ORDEM.map((cadencia) => ({
        cadencia,
        itens: crons
          .filter((cron) => cadenciaDoCron(cron.expressao) === cadencia)
          .sort((a, b) => ordemDeExecucao(a.expressao) - ordemDeExecucao(b.expressao)),
      })).filter((grupo) => grupo.itens.length > 0),
    [crons],
  );

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={4} altura="tw-h-32" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar o catálogo de crons."
          detalhe={erro}
          aoTentarNovamente={() => void carregar('inicial')}
        />
      );
    }

    /*
     * Defensivo: o catálogo é declarativo no backend e hoje tem 13 entradas, então
     * vazio significa que alguém esvaziou a lista de lá. Mesmo assim o texto é
     * DIFERENTE do de erro — as duas situações pedem coisas opostas de quem lê.
     */
    if (crons.length === 0) {
      return (
        <EmptyState
          icone="relogio"
          titulo="Nenhum cron cadastrado no catálogo"
          descricao="O catálogo é declarado no código do backend. Se o sistema tem rotinas automáticas e elas não aparecem aqui, o catálogo está desatualizado."
        />
      );
    }

    return (
      <div className="tw-flex tw-flex-col tw-gap-6">
        {grupos.map((grupo) => (
          <section key={grupo.cadencia}>
            <h2 className="tw-m-0 tw-mb-3 tw-font-marca tw-text-base tw-font-bold tw-text-texto-titulo">
              {ROTULO_CADENCIA[grupo.cadencia]}
            </h2>

            <div className="tw-flex tw-flex-col tw-gap-3">
              {grupo.itens.map((cron) => (
                <CardDoCron key={cron.id} cron={cron} cadencia={grupo.cadencia} />
              ))}
            </div>
          </section>
        ))}
      </div>
    );
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader
          titulo="Crons do Sistema"
          sobretitulo="Administração (TI)"
          aoVoltar={voltar}
          acaoDireita={
            <Botao
              variante="secundario"
              tamanho="md"
              iconeEsquerda="recarregar"
              carregando={atualizando}
              disabled={estado === 'carregando' || atualizando}
              /* O rótulo some no mobile, então o nome acessível vem daqui — senão
                 o botão fica sendo só um ícone para quem usa leitor de tela. */
              aria-label="Atualizar"
              onClick={() => void carregar('atualizar')}
            >
              <span className="tw-hidden lg:tw-inline">Atualizar</span>
            </Botao>
          }
        />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-6xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-flex tw-flex-col tw-gap-4" aria-busy={atualizando}>
              {/* Texto preservado da tela antiga: explica por que os horários são
                  os que são, e é o que os grupos abaixo agora mostram. */}
              <Nota>
                Todas as rotinas automáticas do SAAC: quando rodam, o que fazem e o que acontece
                quando falham. Os horários seguem uma ordem deliberada — espelhos de apoio primeiro
                (6h), depois termos, produtos e cotas, e por fim o rateio (8h); os envios ao SGA
                rodam a cada 2 horas em minutos escalonados para não disputar a API.
              </Nota>

              {/*
                Regra do backend que a tela não tinha como contar: só cotas, produtos
                e descoberta gravam execução em tabela. Sem isto, a ausência de
                "Última execução" nos outros 10 se lê como "nunca rodou".
              */}
              <p className="tw-m-0 tw-text-xs tw-leading-relaxed tw-text-texto-suave">
                <strong className="tw-font-bold">Última execução</strong> só aparece nas rotinas que
                gravam histórico (cotas, produtos e descoberta de associados). Para as demais, o
                sinal de falha é o e-mail de erro ao TI.
              </p>

              {atualizadoEm && estado === 'ok' && (
                <p className="tw-m-0 tw-text-xs tw-text-texto-desabilitado" aria-live="polite">
                  Atualizado em {formatarDataHora(atualizadoEm)}
                </p>
              )}

              {conteudo()}
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

/**
 * Um cron do catálogo.
 *
 * 📌 Função local, não componente do kit: ela conhece o shape `CronCatalogoItem` e
 * tem **um** consumidor — primitivo sem consumidor plural não nasce
 * (`13-ui-kit.md`). É o mesmo arranjo do `CardDoVeiculo` na tela de oficina.
 */
const CardDoCron: React.FC<{ cron: CronCatalogoItem; cadencia: CadenciaCron }> = ({
  cron,
  cadencia,
}) => (
  /* Desativado sobre fundo neutro: distingue sem esmaecer — o card continua
     legível, porque é nele que está escrito por que a rotina saiu do ar. */
  <Card className={`tw-p-4 ${cadencia === 'DESATIVADO' ? 'tw-bg-superficie' : ''}`}>
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
      <h3 className="tw-m-0 tw-mr-auto tw-font-marca tw-text-[15px] tw-font-bold tw-text-texto-titulo">
        {cron.nome}
      </h3>

      <Chip variante={cron.ativo ? 'ok' : 'neutro'}>{cron.ativo ? 'Ativo' : 'Desativado'}</Chip>

      {/* A agenda é ESTADO da rotina, não pendência nem sucesso — por isso
          `neutro`. Antes era um chip azul, cor que não existe nos tokens. */}
      <Chip variante="neutro">{cron.agenda}</Chip>
    </div>

    {cron.ativo && (
      /* ⚠️ "(calculada da agenda)" estava só no `title` do chip. É a ressalva que
         impede o TI de ler a data como uma promessa do scheduler: ela é derivada
         da expressão cron, não do que o servidor tem agendado. */
      <p className="tw-m-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
        Próxima execução (calculada da agenda): {formatarDataHora(cron.proximaExecucao)}
      </p>
    )}

    <p className="tw-m-0 tw-mt-2.5 tw-text-sm tw-leading-relaxed tw-text-texto-corpo">
      {cron.descricao}
    </p>

    <p className="tw-m-0 tw-mt-2 tw-text-xs tw-leading-relaxed tw-text-texto-suave">
      <strong className="tw-font-bold tw-text-texto-corpo">Em caso de falha:</strong>{' '}
      {cron.emFalha}
    </p>

    {cron.ultimaExecucao && <UltimaExecucao execucao={cron.ultimaExecucao} />}

    {cron.observacao && (
      /* Sem `aria-live`: é texto estático do catálogo, e anunciá-lo a cada render
         vizinho seria ruído (regra do `Nota`). */
      <Nota variante="atencao" className="tw-mt-2.5">
        {cron.observacao}
      </Nota>
    )}

    {/* Módulo e expressão FICAM: é por eles que o TI cruza a tela com o código. */}
    <p className="tw-m-0 tw-mt-2.5 tw-font-mono tw-text-[11px] tw-text-texto-desabilitado">
      módulo: {cron.modulo}
      {cron.expressao ? ` · cron: ${cron.expressao}` : ''}
    </p>
  </Card>
);

/**
 * O desfecho da última execução registrada.
 *
 * ⚠️ A fase só diz algo **enquanto a execução corre** — depois dela, "fase
 * VINCULOS" descreve um passado que já não muda nada. Mesma regra do
 * `PainelExecucaoSync` (at-67), de onde ela veio.
 */
const UltimaExecucao: React.FC<{
  execucao: NonNullable<CronCatalogoItem['ultimaExecucao']>;
}> = ({ execucao }) => {
  const rotulo = rotuloStatusExecucao(execucao.status);

  return (
    <div className="tw-mt-2.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
      <span className="tw-text-xs tw-font-bold tw-text-texto-corpo">Última execução:</span>

      <Chip variante={rotulo.variante}>{rotulo.texto}</Chip>

      <span className="tw-text-xs tw-text-texto-suave">
        disparo {rotuloOrigemExecucao(execucao.origem)} ·{' '}
        {formatarDataHora(execucao.iniciadoEm)}
        {execucao.status === 'EM_ANDAMENTO' && execucao.fase ? ` · fase ${execucao.fase}` : ''}
      </span>

      {/* O erro já aparece por extenso — era um `title` sobre o texto truncado. */}
      {execucao.erro && (
        <span className="tw-w-full tw-text-xs tw-font-semibold tw-text-danger">
          Erro: {execucao.erro}
        </span>
      )}
    </div>
  );
};

export default CronsPage;
